/**
 * EditFilmstrip — Tank Redesign Editor variant A bottom strip.
 *
 * Source: design_handoff_tank/tank-redesign/screens-editor.jsx § FaceBar
 * (the editor mockup names the bottom photo-strip "FaceBar"; it's
 * actually a photo navigation strip, not a face strip — the per-photo
 * face row already has its own component in Tank named `FaceBar`).
 *
 * 80 px tall horizontal scroller. Each tile shows a photo placeholder
 * (or actual thumbnail when wired to real data), the redesign
 * StatusPill in the top-right, and the file ID at the bottom-left.
 * Current photo is highlighted with a phosphor border + soft outer
 * glow.
 *
 * Replaces the prior left-column `PhotoListSidebar` on the Edit page.
 * The same set of photos and selection model is used; only the
 * physical placement (left → bottom) and visual treatment changes.
 */

import { useMemo, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { StatusPill } from '../../../../shared/components/redesign';
import { getRedesignStatusKind, getStatusLabel, getStatusTooltip } from '../../../../utils/statusUtils';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import styles from './EditFilmstrip.module.css';

const TILE_W = 90;

export default function EditFilmstrip({
  photos = [],
  selectedImageId,
  onSelectPhoto,
  resolveUrl,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}) {
  const scrollRef = useRef(null);

  // Auto-scroll the active tile into view.
  useEffect(() => {
    if (!selectedImageId || !scrollRef.current) return;
    const node = scrollRef.current.querySelector(`[data-image-id="${selectedImageId}"]`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedImageId]);

  // Scroll-triggered lazy load — sentinel at the right edge of the strip.
  // Same pattern as PhotoListSidebar.
  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    loading: loadingMore,
    onLoadMore: onLoadMore || (() => {}),
    enabled: !!onLoadMore,
    rootMargin: '0px 800px 0px 0px',
  });

  const items = useMemo(
    () => (Array.isArray(photos) ? photos : []),
    [photos],
  );

  const scrollBy = (dx) => {
    scrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });
  };

  return (
    <div className={styles.strip}>
      <button
        type="button"
        className={styles.scrollBtn}
        onClick={() => scrollBy(-TILE_W * 4)}
        title="Scroll left"
        aria-label="Scroll left"
      >
        <ChevronLeftIcon style={{ width: 14, height: 14 }} />
      </button>

      <div className={styles.scroll} ref={scrollRef}>
        {items.length === 0 && (
          <div className={styles.empty}>No photos in current filter.</div>
        )}
        {items.map((p) => {
          const active = p.image_id === selectedImageId;
          const previewUrl = p.preview_url
            ? (resolveUrl ? resolveUrl(p.preview_url) : p.preview_url)
            : null;
          return (
            <button
              key={p.image_id}
              type="button"
              data-image-id={p.image_id}
              className={`${styles.tile} ${active ? styles.tileActive : ''}`}
              onClick={() => onSelectPhoto?.(p.image_id)}
              title={p.filename || p.image_id}
            >
              {previewUrl ? (
                <img src={previewUrl} alt="" className={styles.tileImg} loading="lazy" />
              ) : (
                <div className={styles.tilePlaceholder} />
              )}
              <span className={styles.tileStatus} title={getStatusTooltip(p)}>
                <StatusPill kind={getRedesignStatusKind(p)} compact>
                  {getStatusLabel(p)}
                </StatusPill>
              </span>
              <span className={styles.tileId}>{p.filename || p.image_id?.slice(0, 8)}</span>
            </button>
          );
        })}
        {hasMore && (
          <div ref={sentinelRef} className={styles.sentinel}>
            {loadingMore ? 'Loading more…' : ''}
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.scrollBtn}
        onClick={() => scrollBy(TILE_W * 4)}
        title="Scroll right"
        aria-label="Scroll right"
      >
        <ChevronRightIcon style={{ width: 14, height: 14 }} />
      </button>
    </div>
  );
}
